import type { 
    SiteMetric, 
    SiteTimeSeries, 
    HeatmapCell, 
    AnalyticsSummary,
    TimePeriod 
} from '../types';

const seededRandom = (seed: string) => {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
        hash = ((hash << 5) - hash) + seed.charCodeAt(i);
        hash = hash & hash;
    }
    return () => {
        hash = ((hash * 9301 + 49297) % 233280) / 233280;
        return hash;
    };
};

const generateTimeSeries = (
    siteId: string,
    period: TimePeriod,
    baseUptime: number,
    baseResponse: number
): SiteTimeSeries => {
    const rng = seededRandom(`${siteId}-${period}`);
    const points: SiteTimeSeries['data'] = [];
    
    const hours = period === '24h' ? 24 : period === '7d' ? 168 : period === '30d' ? 720 : 2160;
    const interval = period === '24h' ? 1 : period === '7d' ? 4 : 24;
    
    const now = Date.now();
    
    for (let i = hours; i >= 0; i -= interval) {
        const timestamp = new Date(now - i * 3600000).toISOString();
        const variation = (rng() - 0.5) * 0.1;
        
        points.push({
            timestamp,
            uptime: Math.min(100, Math.max(90, baseUptime + variation * 10)),
            responseTime: Math.max(50, baseResponse + (rng() - 0.5) * 200),
            requests: Math.floor(1000 + rng() * 5000),
            errors: Math.floor(rng() * 50),
        });
    }
    
    return {
        siteId,
        siteName: `Site ${siteId}`,
        data: points,
    };
};

const generateHeatmap = (siteId: string, period: TimePeriod): HeatmapCell[] => {
    const rng = seededRandom(`heatmap-${siteId}-${period}`);
    const cells: HeatmapCell[] = [];
    const days = period === '24h' ? 1 : period === '7d' ? 7 : 30;

    const now = new Date();

    for (let d = 0; d < days; d++) {
        const date = new Date(now.getTime() - d * 86400000).toISOString().split('T')[0];

        for (let h = 0; h < 24; h += 2) {
            const uptime = 85 + rng() * 15;
            let status: HeatmapCell['status'] = 'excellent';
            
            if (uptime < 95) status = 'good';
            if (uptime < 90) status = 'warning';
            if (uptime < 85) status = 'critical';
            
            cells.push({ date, hour: h, uptime, status });
        }
    }

    return cells;
};

export const MOCK_SITES: SiteMetric[] = [
    {
        siteId: 'shop',
        siteName: 'shop.example.ru',
        uptime: 99.97,
        avgResponseTime: 245,
        totalRequests: 154320,
        errorRate: 0.03,
        incidents: 1,
        lastCheck: new Date().toISOString(),
    },
    {
        siteId: 'lk',
        siteName: 'lk.example.ru',
        uptime: 99.85,
        avgResponseTime: 312,
        totalRequests: 89300,
        errorRate: 0.15,
        incidents: 3,
        lastCheck: new Date().toISOString(),
    },
    {
        siteId: 'api',
        siteName: 'api.example.ru',
        uptime: 97.42,
        avgResponseTime: 1200,
        totalRequests: 456000,
        errorRate: 2.58,
        incidents: 12,
        lastCheck: new Date().toISOString(),
    },
    {
        siteId: 'blog',
        siteName: 'blog.example.ru',
        uptime: 99.99,
        avgResponseTime: 189,
        totalRequests: 67000,
        errorRate: 0.01,
        incidents: 0,
        lastCheck: new Date().toISOString(),
    },
    {
        siteId: 'cdn',
        siteName: 'cdn.example.ru',
        uptime: 99.95,
        avgResponseTime: 56,
        totalRequests: 890000,
        errorRate: 0.05,
        incidents: 2,
        lastCheck: new Date().toISOString(),
    },
];

export const generateMockAnalytics = async (
    period: TimePeriod,
    siteIds: string[]
) => {
    await new Promise(r => setTimeout(r, 300 + Math.random() * 400));
    
    const selectedSites = siteIds.length > 0 
        ? MOCK_SITES.filter(s => siteIds.includes(s.siteId))
        : MOCK_SITES;
    
    const timeSeries = selectedSites.map(site => 
        generateTimeSeries(site.siteId, period, site.uptime, site.avgResponseTime)
    );
    
    const heatmaps = selectedSites.map(site => ({
        siteId: site.siteId,
        siteName: site.siteName,
        cells: generateHeatmap(site.siteId, period),
    }));
    
    const overallUptime = selectedSites.reduce((sum, s) => sum + s.uptime, 0) / selectedSites.length;
    const avgResponse = selectedSites.reduce((sum, s) => sum + s.avgResponseTime, 0) / selectedSites.length;
    
    const summary: AnalyticsSummary = {
        overallUptime: Number(overallUptime.toFixed(2)),
        avgResponseTime: Math.round(avgResponse),
        totalIncidents: selectedSites.reduce((sum, s) => sum + s.incidents, 0),
        sitesMonitored: selectedSites.length,
        periodStart: timeSeries[0]?.data[0]?.timestamp || new Date().toISOString(),
        periodEnd: new Date().toISOString(),
        slaTarget: SLA_TARGETS.premium,
        slaMet: overallUptime >= SLA_TARGETS.premium,
    };
    
    return {
        summary,
        sites: selectedSites,
        timeSeries,
        heatmaps,
    };
};

import { SLA_TARGETS } from '../constants';